import type { Setting } from '../../models/Setting';
import { AuditActionType } from '../../models/enums';
import { apiClient } from '../api/apiClient';
import { createId, delay, readCollection, seedCollection, writeCollection } from './dataverseClient';
import { auditLogService } from './auditLogService';
import { settingsSeed } from './mockData';

const ENTITY = 'cr_crm_setting';

class SettingService {
  private ensureSeed(): void {
    seedCollection(ENTITY, settingsSeed);
  }

  async list(): Promise<Setting[]> {
    if (apiClient.isConfigured()) {
      const res = await apiClient.get<Setting[] | { items: Setting[] }>('/api/settings');
      return Array.isArray(res) ? res : res.items ?? [];
    }
    this.ensureSeed();
    await delay();
    return readCollection<Setting>(ENTITY).sort((a, b) => a.cr_crm_label.localeCompare(b.cr_crm_label));
  }

  async getByKey(key: string): Promise<Setting | null> {
    if (apiClient.isConfigured()) {
      try {
        return await apiClient.get<Setting>(`/api/settings/${key}`);
      } catch {
        return null;
      }
    }
    this.ensureSeed();
    await delay();
    return readCollection<Setting>(ENTITY).find((s) => s.cr_crm_key === key) ?? null;
  }

  async upsert(key: string, value: string, metadata?: Partial<Setting>): Promise<Setting> {
    if (apiClient.isConfigured()) {
      return apiClient.put<Setting>(`/api/settings/${key}`, { value, ...metadata });
    }
    this.ensureSeed();
    await delay();
    const settings = readCollection<Setting>(ENTITY);
    const existing = settings.find((s) => s.cr_crm_key === key);
    const next: Setting = {
      cr_crm_settingid: existing?.cr_crm_settingid ?? createId('setting'),
      cr_crm_key: key,
      cr_crm_label: metadata?.cr_crm_label ?? existing?.cr_crm_label ?? key,
      cr_crm_value: value,
      cr_crm_category: metadata?.cr_crm_category ?? existing?.cr_crm_category ?? 'General',
      cr_crm_settingtype: metadata?.cr_crm_settingtype ?? existing?.cr_crm_settingtype ?? 'string',
      cr_crm_description: metadata?.cr_crm_description ?? existing?.cr_crm_description,
      modifiedon: new Date().toISOString(),
    };
    writeCollection(ENTITY, [...settings.filter((s) => s.cr_crm_key !== key), next]);
    await auditLogService.record({
      actionType: AuditActionType.Update,
      entityName: 'Setting',
      entityId: next.cr_crm_settingid,
      description: `Setting ${next.cr_crm_label} updated.`,
    });
    return next;
  }
}

export const settingService = new SettingService();
